import { useState, useEffect, useCallback } from "react";
import { Button } from "../ui/button";

const GRID_SIZE = 4;
const INITIAL_PATTERN_LENGTH = 3;

function generatePattern(length) {
  const pattern = [];
  for (let i = 0; i < length; i++) {
    let cell;
    do { cell = Math.floor(Math.random() * GRID_SIZE * GRID_SIZE); } while (pattern.includes(cell));
    pattern.push(cell);
  }
  return pattern;
}

export default function MemoryGrid({ onComplete }) {
  const [state, setState] = useState("idle"); // idle, showing, input, correct, wrong, finished
  const [level, setLevel] = useState(1);
  const [pattern, setPattern] = useState([]);
  const [playerInput, setPlayerInput] = useState([]);
  const [showingIdx, setShowingIdx] = useState(-1);
  const [lives, setLives] = useState(3);
  const [maxLevel, setMaxLevel] = useState(0);

  const startLevel = useCallback((lvl) => {
    const patternLength = INITIAL_PATTERN_LENGTH + lvl - 1;
    const newPattern = generatePattern(Math.min(patternLength, GRID_SIZE * GRID_SIZE - 1));
    setPattern(newPattern);
    setPlayerInput([]);
    setState("showing");
    
    // Flash each cell sequentially
    let i = 0;
    const interval = setInterval(() => {
      setShowingIdx(newPattern[i]);
      setTimeout(() => setShowingIdx(-1), 500);
      i++;
      if (i >= newPattern.length) {
        clearInterval(interval);
        setTimeout(() => setState("input"), 600);
      }
    }, 700);
  }, []);

  const handleCellClick = (idx) => {
    if (state !== "input") return;
    const nextInput = [...playerInput, idx];
    setPlayerInput(nextInput);

    const step = nextInput.length - 1;
    if (nextInput[step] !== pattern[step]) {
      // Wrong
      const newLives = lives - 1;
      setLives(newLives);
      if (newLives <= 0) {
        setState("finished");
      } else {
        setState("wrong");
        setTimeout(() => startLevel(level), 1500);
      }
      return;
    }

    if (nextInput.length === pattern.length) {
      // Level complete
      const nextLevel = level + 1;
      setMaxLevel(Math.max(maxLevel, nextLevel - 1));
      setLevel(nextLevel);
      setState("correct");
      setTimeout(() => startLevel(nextLevel), 1200);
    }
  };

  const score = Math.max(0, maxLevel * 100 + (lives * 50));

  if (state === "finished") {
    return (
      <div data-testid="memory-result" className="text-center py-8 space-y-6">
        <div className="w-28 h-28 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mx-auto shadow-xl">
          <span className="text-3xl font-bold text-white">{score}</span>
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900">Memory Score</h3>
          <p className="text-gray-500">Reached Level {maxLevel} | Pattern length: {INITIAL_PATTERN_LENGTH + maxLevel - 1}</p>
        </div>
        <Button data-testid="save-memory-score" onClick={() => onComplete(score, maxLevel * 10)} className="bg-emerald-600 hover:bg-emerald-700">
          Save Score & Return
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="memory-grid-game" className="flex flex-col items-center py-6 space-y-4">
      {state === "idle" && (
        <>
          <p className="text-sm text-gray-600 text-center max-w-md">Watch the pattern, then tap the cells in the same order. Each level adds more cells to remember.</p>
          <Button data-testid="start-memory" onClick={() => startLevel(1)} className="bg-amber-600 hover:bg-amber-700 text-lg px-8 py-3">Start Game</Button>
        </>
      )}

      {state !== "idle" && (
        <>
          <div className="flex items-center justify-between w-full max-w-xs">
            <span className="text-sm font-bold text-gray-700">Level {level}</span>
            <div className="flex gap-1">
              {Array.from({ length: 3 }).map((_, i) => (
                <span key={i} className={`w-5 h-5 rounded-full ${i < lives ? 'bg-red-500' : 'bg-gray-200'}`} />
              ))}
            </div>
            <span className="text-sm text-gray-500">{pattern.length} cells</span>
          </div>

          {state === "correct" && <p className="text-emerald-600 font-bold animate-pulse">Correct! Next level...</p>}
          {state === "wrong" && <p className="text-red-500 font-bold">Wrong! Try again...</p>}
          {state === "showing" && <p className="text-blue-600 font-semibold">Watch the pattern...</p>}
          {state === "input" && <p className="text-gray-600 text-sm">Tap the cells in order ({playerInput.length}/{pattern.length})</p>}

          <div className="grid grid-cols-4 gap-2" style={{ width: 240 }}>
            {Array.from({ length: GRID_SIZE * GRID_SIZE }).map((_, idx) => {
              const isShowing = showingIdx === idx;
              const isCorrectInput = playerInput.includes(idx) && pattern[playerInput.indexOf(idx)] === idx;
              return (
                <button
                  key={idx}
                  onClick={() => handleCellClick(idx)}
                  disabled={state !== "input"}
                  className={`w-14 h-14 rounded-lg transition-all duration-200 border-2 ${
                    isShowing ? 'bg-blue-500 border-blue-600 scale-110' :
                    isCorrectInput ? 'bg-emerald-400 border-emerald-500' :
                    state === "input" ? 'bg-white border-gray-200 hover:border-blue-400 hover:bg-blue-50 cursor-pointer' :
                    'bg-gray-100 border-gray-200'
                  }`}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
